import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getDataSourceById, toUiDataSource, toRequestDto, updateDataSource } from 'DataSources/datasourcesApi';

/**
 * The owner's object choice for a Salesforce org (config.selectedObjects): which objects discovery
 * samples (since TDM contract 3.0 a subset's objects are its data graph's, not this choice). Edited on
 * the Fingerprint page's object list and object page (since 2026-11-04; the Data Graph's ticks are the
 * graph's own list, see DataGraph/useGraphDraft.graphChoice), which are never mounted together — so a
 * choice not yet saved is kept here per source and the other screen picks it up (tick on the list, open
 * an object, add another, come back, save once). ObjectChoiceBar draws either kind.
 */
const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

const unsavedBySource = new Map();
/** Tests only: forget every remembered unsaved choice. */
export const resetUnsavedChoices = () => unsavedBySource.clear();

/**
 * `source` is the platform row (toUiDataSource shape) and seeds the choice; it may arrive after
 * the first render. Saving writes config.selectedObjects through the ordinary data-source PUT (the
 * platform carries secrets forward) and calls `onSaved(list)` so the page can refresh its copy.
 * With `enabled` false the hook is inert (nothing remembered, nothing dirty).
 */
export function useObjectChoice({ dataSourceId, source, enabled = true, onSaved }) {
  const key = String(dataSourceId);
  const sourceChoice = useMemo(() => new Set(Array.isArray(source && source.selectedObjects) ? source.selectedObjects : []),
    [source]);
  // The platform's copy as this screen last knew it: seeded from the source row, updated on every
  // save, re-seeded when the row changes. A remembered unsaved choice wins over the re-seed.
  const [saved, setSaved] = useState(() => new Set(sourceChoice));
  const [picked, setPicked] = useState(() => (enabled && unsavedBySource.get(key)) || new Set(sourceChoice));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    setSaved(new Set(sourceChoice));
    setPicked((enabled && unsavedBySource.get(key)) || new Set(sourceChoice));
  }, [sourceChoice, key, enabled]);

  const dirty = enabled && !sameSet(picked, saved);
  useEffect(() => {
    if (!enabled) return;
    if (dirty) unsavedBySource.set(key, picked); else unsavedBySource.delete(key);
  }, [enabled, key, picked, dirty]);

  const toggle = (table) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(table)) next.delete(table); else next.add(table);
    return next;
  });
  const pickAll = (tables, on) => setPicked((prev) => {
    const next = new Set(prev);
    tables.forEach((t) => { if (on) next.add(t); else next.delete(t); });
    return next;
  });
  const save = async (list) => {
    setSaving(true);
    setSaveError(null);
    try {
      const dto = await getDataSourceById(dataSourceId);
      const row = toUiDataSource(dto);
      await updateDataSource(dataSourceId, toRequestDto({ ...row, selectedObjects: list }));
      unsavedBySource.delete(key);
      if (!mounted.current) return;
      setSaved(new Set(list));
      setPicked(new Set(list));
      if (onSaved) onSaved(list);
    } catch (e) {
      if (mounted.current) setSaveError((e && e.message) || 'Could not save the object choice.');
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return {
    picked, saved, dirty, saving, saveError, toggle, pickAll,
    save: () => save([...picked].sort()),
    revert: () => setPicked(new Set(saved)),
    clear: () => (saved.size === 0 ? setPicked(new Set()) : save([])),
  };
}

/**
 * The "N of M chosen" bar with Save / Revert / Clear. With `current` (the object page) it also
 * offers Add / Remove for that one object — an edit like a ticked box, saved with Save choice.
 */
export const ObjectChoiceBar = ({ choice, total, itemNoun = 'objects', current }) => {
  const { picked, saved, dirty, saving, saveError } = choice;
  const has = current ? picked.has(current) : false;
  // `kind` graph (a data graph's own object list, saved with the page's settings; 2026-11-04) or discover
  // (the source's Discover scope). `immediate`: a toggle saves at once (the graph's object page).
  const graph = choice.kind === 'graph';
  const immediate = Boolean(choice.immediate);
  const what = graph ? 'in this data graph' : 'chosen';
  const where = graph ? 'this data graph' : 'the chosen objects';
  return (
    <div className={`sfn-choice${dirty ? ' sfn-choice--dirty' : ''}`} data-testid='sf-object-choice'>
      <span>
        <strong>{picked.size}</strong>{total != null ? ` of ${total}` : ''} {itemNoun} {what}
        {picked.size === 0 && !graph && <span className='sfn-muted'> — none chosen means discovery samples every object</span>}
        {picked.size === 0 && graph && <span className='sfn-muted'> — none listed: only the key table&apos;s selected rows would be saved</span>}
        {dirty && <span className='sfn-muted'> · unsaved</span>}
      </span>
      {current && (
        <button type='button' className={`sfn-btn${has ? '' : ' sfn-btn--primary'}`} data-testid='sf-object-choice-toggle'
          disabled={saving} onClick={() => choice.toggle(current)}
          title={has ? `Take ${current} out of ${where}` : `Add ${current} to ${where}`}>
          {saving && immediate ? 'Saving…' : has ? `Remove ${current}` : `Add ${current}`}
        </button>
      )}
      {!immediate && (
        <button type='button' className={`sfn-btn${current && !has ? '' : ' sfn-btn--primary'}`} data-testid='sf-object-choice-save'
          disabled={!dirty || saving} onClick={choice.save}>
          {saving ? 'Saving…' : graph ? 'Save settings' : 'Save choice'}
        </button>
      )}
      {!immediate && dirty && (
        <button type='button' className='sfn-btn' data-testid='sf-object-choice-revert'
          disabled={saving} onClick={choice.revert}>
          Revert
        </button>
      )}
      {!immediate && (
        <button type='button' className='sfn-btn' data-testid='sf-object-choice-clear'
          disabled={saving || (picked.size === 0 && saved.size === 0)} onClick={choice.clear}>
          Clear
        </button>
      )}
      {saveError && <span className='sfn-muted' style={{ color: 'var(--danger)' }}>{saveError}</span>}
      {!dirty && saved.size > 0 && !graph && (
        <span className='sfn-muted'>Discovery samples only these. A subset's objects are its data graph's, not this choice.</span>
      )}
      {!dirty && saved.size > 0 && graph && (
        <span className='sfn-muted'>A subset through this data graph saves these; its Test shows how each is reached.</span>
      )}
    </div>
  );
};
